/**
 * Visualization Validation
 * 
 * Validates VisualizationSignal structure and data integrity.
 */

import type { VisualizationSignal } from '../../types/visualization.js';
import { log } from '../../services/logging/index.js';

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a visualization signal
 * Checks required fields and basic structure
 */
export function validateVisualizationSignal(signal: unknown): ValidationResult {
  // Check if signal is an object
  if (!signal || typeof signal !== 'object') {
    return {
      valid: false,
      error: 'Visualization signal must be an object'
    };
  }

  const viz = signal as Partial<VisualizationSignal>;

  // Check required fields
  if (!viz.id || typeof viz.id !== 'string') {
    return {
      valid: false,
      error: 'Visualization signal must have a valid id (string)'
    };
  }

  if (!viz.type || typeof viz.type !== 'string') {
    return {
      valid: false,
      error: 'Visualization signal must have a valid type (string)'
    };
  }

  if (!viz.title || typeof viz.title !== 'string') {
    return {
      valid: false,
      error: 'Visualization signal must have a valid title (string)'
    };
  }

  // Check data field exists and is valid
  if (viz.data === undefined || viz.data === null) {
    return {
      valid: false,
      error: 'Visualization signal must have a data field'
    };
  }

  // Data should be an object or array
  if (typeof viz.data !== 'object') {
    return {
      valid: false,
      error: 'Visualization signal data must be an object or array'
    };
  }

  // If data is an array, it should not be empty (unless explicitly allowed)
  if (Array.isArray(viz.data) && viz.data.length === 0) {
    log.warn(`[Visualization] Empty data array for visualization ${viz.id} of type ${viz.type}`);
    // Not invalid, but worth warning
  }

  return { valid: true };
}

/**
 * Validate data structure for specific visualization types
 * This is a basic check - more detailed validation can be added per type
 */
export function validateDataStructure(
  type: string,
  data: any
): ValidationResult {
  // Basic checks that apply to most types
  if (data === null || data === undefined) {
    return {
      valid: false,
      error: `Data is null or undefined for type ${type}`
    };
  }

  // For array-based visualizations, check array structure
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return {
        valid: false,
        error: `Empty data array for type ${type}`
      };
    }

    // Check if array items are objects (common pattern)
    if (data.length > 0 && typeof data[0] !== 'object') {
      // Some visualizations might accept primitive arrays, so this is a warning
      log.warn(`[Visualization] Array items are not objects for type ${type}`);
    }
  }

  // Type-specific validation can be added here
  // For now, we'll do basic validation and let renderers handle specifics

  return { valid: true };
}

/**
 * Full validation: structure + data
 */
export function validateFull(signal: unknown): ValidationResult {
  const structureResult = validateVisualizationSignal(signal);
  if (!structureResult.valid) {
    return structureResult;
  }

  const viz = signal as VisualizationSignal;
  const dataResult = validateDataStructure(viz.type, viz.data);
  if (!dataResult.valid) {
    return dataResult;
  }

  return { valid: true };
}


